// Guess — გამოიცანი as an online room game: every player on their own phone (and, if they like,
// a TV for the pictures). Same rules as the one-phone duel; the server owns the chess clock and
// only the active player's microphone counts. The picture sequence and its answers never enter
// this state: they live in the server's Guess match (ContentAccess.guess); the state only carries
// opaque picture links.
//
// Stages: check (each player passes the 5-word voice check on their own device) → intro (3-2-1)
// → play → over. The end of speech and the judge's verdict come from the server's utterance route
// as system messages, so a player can never stop their own clock without speaking.

import type { GuessCategoryRef } from "../content";
import { activePlayers, type Engine, type EngineCtx, type TimedState } from "../engine";
import type { GuessAnswerReveal, GuessPicture, GuessResult } from "../guess/api";
import { applyClock, createClock, expiresAt, GUESS_MAX_PLAYERS, GUESS_PENALTY_MS, GUESS_TIME_MS, type ClockEvent, type ClockState, type ClockVerdict } from "../guess/clock";
import { buildGuessResult } from "../guess/result";
import type { GameResultSummary } from "../types";

export interface GuessSettings {
  category: string;
}

export type GuessStage = "check" | "intro" | "play" | "over";
export type GuessFeedbackKind = ClockVerdict | "out";

export interface GuessFeedback {
  id: number;
  kind: GuessFeedbackKind;
  seat: number;
  heard: string;
  reason: string;
  answer?: GuessAnswerReveal;
  penalty: boolean;
}

export interface GuessSeat {
  id: string;
  name: string;
  avatar: number;
  userId: number | null;
}

export interface GuessLogEntry {
  seat: number;
  image: number;
  verdict: ClockVerdict;
  heard: string;
  reason: string;
  answer?: GuessAnswerReveal;
}

export interface GuessRoomState extends TimedState {
  stage: GuessStage;
  matchId: string;
  category: GuessCategoryRef;
  pictures: GuessPicture[];
  seats: GuessSeat[];
  /** players whose voice check passed → the language they tested in */
  checked: Record<string, "ka" | "en">;
  /** voice tests already used in this room (one test, one seat) — never in a view */
  tests: string[];
  clock: ClockState;
  events: ClockEvent[];
  shown: number;
  speaking: { seat: number; until: number } | null;
  feedback: GuessFeedback | null;
  fbSeq: number;
  result: GuessResult | null;
  verified: boolean | null;
  introAt: number | null;
  /** utterances judged in this match (for the results screen) */
  log: GuessLogEntry[];
}

export interface GuessView {
  stage: GuessStage;
  category: GuessCategoryRef;
  seats: { id: string; name: string; avatar: number; checked: boolean; lang: "ka" | "en" | null; present: boolean }[];
  mySeat: number;
  clock: Omit<ClockState, "handled">;
  timeMs: number;
  penaltyMs: number;
  /** pictures around the current one (each carries its absolute index `i`), for preloading */
  window: GuessPicture[];
  /** absolute index of the picture on screen, null between pictures */
  current: number | null;
  speaking: number | null;
  feedback: GuessFeedback | null;
  result: GuessResult | null;
  verified: boolean | null;
  introAt: number | null;
  introMs: number;
  log: GuessLogEntry[];
}

export const GUESS_INTRO_MS = 3600;
/** The clock starts a moment after the picture is sent, so slower phones do not lose time. */
export const GUESS_SHOW_GRACE_MS = 400;
/** No verdict after this long → technical pause (nobody loses time). */
const CHECK_TIMEOUT_MS = 16_000;
/** A player still talking when their clock hits zero gets the answer judged first (at most this long). */
const SPEAKING_HOLD_MS = 8000;
/** How far back the end of speech may lie when the audio reaches the server. */
export const GUESS_MAX_BACKDATE_MS = 2500;
const FEEDBACK_MS: Partial<Record<GuessFeedbackKind, number>> = { correct: 1300, wrong: 1050, skip: 1000, out: 1500 };
const VERDICTS: ClockVerdict[] = ["correct", "wrong", "skip", "unclear", "ignored", "error"];

function dispatch(s: GuessRoomState, e: ClockEvent): boolean {
  const before = s.clock;
  const next = applyClock(before, e);
  if (next === before) return false;
  s.clock = next;
  s.events.push(e);
  if (e.t === "shown") s.shown += 1;
  // somebody ran out of time (by the clock or by a penalty) — not someone who left
  const out = next.outOrder.filter((i) => !before.outOrder.includes(i));
  if (out.length && e.t !== "forfeit") feedback(s, { kind: "out", seat: out[out.length - 1], heard: "", reason: "", penalty: false });
  return true;
}

function feedback(s: GuessRoomState, f: Omit<GuessFeedback, "id">) {
  s.fbSeq += 1;
  s.feedback = { ...f, id: s.fbSeq };
}

function finish(s: GuessRoomState, ctx: EngineCtx) {
  s.stage = "over";
  s.deadline = null;
  s.phaseMs = 0;
  s.speaking = null;
  if (s.clock.endedBy === "abort") {
    try {
      ctx.content.guess?.abort(s.matchId);
    } catch {
      /* nothing to save */
    }
    return;
  }
  const players = s.seats.map((p) => ({ name: p.name, avatar: p.avatar, guest: !p.userId }));
  s.result = buildGuessResult(players, s.clock, s.shown);
  try {
    s.verified = ctx.content.guess ? ctx.content.guess.finish(s.matchId, s.events) : null;
  } catch {
    s.verified = null;
  }
}

/** Put the next picture on screen (or end the match when the bank is empty). */
function show(s: GuessRoomState, ctx: EngineCtx) {
  const img = s.clock.image;
  if (img >= s.pictures.length) {
    dispatch(s, { t: "exhausted", at: ctx.now });
    finish(s, ctx);
    return;
  }
  dispatch(s, { t: "shown", at: ctx.now + GUESS_SHOW_GRACE_MS, image: img });
  schedule(s, ctx.now);
}

/** Set the deadline from the clock: expiry while running, a timeout while checking, the pause between pictures. */
function schedule(s: GuessRoomState, now: number) {
  const c = s.clock;
  if (s.stage !== "play") return;
  switch (c.phase) {
    case "running":
      s.deadline = expiresAt(c);
      s.phaseMs = c.players[c.active]?.remaining ?? 0;
      break;
    case "checking":
      s.deadline = now + CHECK_TIMEOUT_MS;
      break;
    case "transition":
      s.deadline = now + ((s.feedback && FEEDBACK_MS[s.feedback.kind]) ?? 700);
      break;
    default:
      s.deadline = null; // paused (resumed by the players) or over
  }
}

function afterClock(s: GuessRoomState, ctx: EngineCtx) {
  if (s.clock.phase === "over") finish(s, ctx);
  else schedule(s, ctx.now);
}

/** After a pause: a picture that was waiting to be shown appears now; otherwise the clock runs on. */
function afterResume(s: GuessRoomState, ctx: EngineCtx) {
  if (s.clock.phase === "transition") show(s, ctx);
  else schedule(s, ctx.now);
}

const seatOf = (s: GuessRoomState, playerId: string) => s.seats.findIndex((p) => p.id === playerId);
const allChecked = (s: GuessRoomState) => s.seats.every((p, i) => s.clock.players[i]?.out || s.checked[p.id]);

function startIntro(s: GuessRoomState, now: number) {
  s.stage = "intro";
  s.introAt = now;
  s.deadline = now + GUESS_INTRO_MS;
  s.phaseMs = GUESS_INTRO_MS;
}

export const guessEngine: Engine<GuessRoomState, GuessSettings, GuessView> = {
  id: "guess",
  minPlayers: 2,
  maxPlayers: GUESS_MAX_PLAYERS,
  supportsBots: false,

  defaults: () => ({ category: "flags" }),

  sanitize(input, prev) {
    const c = (input as Partial<GuessSettings> | null)?.category;
    return { category: typeof c === "string" && /^[a-z0-9-]{1,40}$/.test(c) ? c : prev.category };
  },

  start(room, ctx) {
    const g = ctx.content.guess;
    if (!g) return "no_content";
    const players = activePlayers(room).filter((p) => !p.isBot).slice(0, GUESS_MAX_PLAYERS);
    if (players.length < 2) return "too_few_players";
    const seats: GuessSeat[] = players.map((p) => ({ id: p.id, name: p.name, avatar: p.avatar, userId: p.userId ?? null }));
    const m = g.createMatch({
      category: room.settings.category,
      lang: room.lang,
      players: seats.map(({ name, avatar, userId }) => ({ name, avatar, userId })),
      roomCode: room.code,
    });
    if (!m || !m.pictures.length) return "no_content";
    // players who already passed the voice check in this room keep it for the rematch
    const prev = room.state as GuessRoomState | null;
    const checked: GuessRoomState["checked"] = {};
    for (const p of seats) if (prev?.checked?.[p.id]) checked[p.id] = prev.checked[p.id];
    const s: GuessRoomState = {
      deadline: null,
      phaseMs: 0,
      stage: "check",
      matchId: m.matchId,
      category: m.category,
      pictures: m.pictures,
      seats,
      checked,
      tests: prev?.tests ?? [],
      clock: createClock(seats.length, GUESS_TIME_MS),
      events: [],
      shown: 0,
      speaking: null,
      feedback: null,
      fbSeq: prev?.fbSeq ?? 0,
      result: null,
      verified: null,
      introAt: null,
      log: [],
    };
    if (allChecked(s)) startIntro(s, ctx.now);
    return s;
  },

  act(room, s, actorId, a, ctx) {
    const seat = seatOf(s, actorId);
    const isHost = room.hostId === actorId;
    switch (a?.t) {
      case "voiceOk": {
        if (s.stage !== "check") return "bad_phase";
        if (seat < 0) return "not_player";
        if (s.checked[actorId]) return s;
        const testId = typeof a.testId === "string" && /^[A-Za-z0-9_-]{6,64}$/.test(a.testId) ? a.testId : undefined;
        if (testId && s.tests.includes(testId)) return "already";
        const lang = ctx.content.guess?.voiceOk({ testId, profile: !testId && a.profile === true, userId: s.seats[seat].userId });
        if (!lang) return "bad_input";
        s.checked[actorId] = lang;
        if (testId) s.tests = [...s.tests, testId].slice(-60);
        if (allChecked(s)) startIntro(s, ctx.now);
        return s;
      }
      case "speaking": {
        const c = s.clock;
        if (s.stage !== "play" || seat !== c.active || c.phase !== "running") return "not_your_turn";
        const exp = expiresAt(c) ?? ctx.now;
        s.speaking = { seat, until: Math.min(ctx.now + SPEAKING_HOLD_MS, exp + SPEAKING_HOLD_MS) };
        return s;
      }
      case "resume": {
        // after a technical pause (the recogniser failed) the host or the player whose turn it is goes on
        const c = s.clock;
        if (s.stage !== "play" || c.phase !== "paused" || c.pauseKind !== "technical") return "bad_phase";
        if (!isHost && seat !== c.active) return "not_host";
        if (!dispatch(s, { t: "resume", at: ctx.now })) return "bad_phase";
        afterResume(s, ctx);
        return s;
      }
      case "quit": {
        if (!isHost) return "not_host";
        if (s.stage === "over") return "bad_phase";
        dispatch(s, { t: "abort", at: ctx.now });
        finish(s, ctx);
        return s;
      }
      default:
        return "bad_input";
    }
  },

  /** Server-internal: the end of an utterance (before judging) and the judge's verdict. */
  system(_room, s, msg, ctx) {
    if (s.stage !== "play" || !msg || typeof msg !== "object") return s;
    const c = s.clock;
    if (msg.t === "speechEnd") {
      if (msg.seat !== c.active || c.phase !== "running" || c.since === null) return s;
      const claimed = Number(msg.at);
      // the end of speech: never before the picture appeared, never in the future, never long ago
      const at = Math.min(ctx.now, Math.max(c.since, ctx.now - GUESS_MAX_BACKDATE_MS, Number.isFinite(claimed) ? claimed : ctx.now));
      if (dispatch(s, { t: "speechEnd", at, id: String(msg.id ?? "") })) {
        s.speaking = null;
        afterClock(s, ctx);
      }
      return s;
    }
    if (msg.t === "verdict") {
      const pending = c.pending;
      if (!pending || pending.id !== msg.id) return s; // late or duplicate
      const v: ClockVerdict = VERDICTS.includes(msg.v) ? msg.v : "error";
      const seat = c.active;
      const heard = String(msg.heard ?? "").slice(0, 120);
      const reason = String(msg.reason ?? "");
      const answer = msg.answer && typeof msg.answer === "object" ? { ka: String(msg.answer.ka ?? ""), en: String(msg.answer.en ?? "") } : undefined;
      // feedback first: the pause before the next picture depends on it
      feedback(s, { kind: v, seat, heard, reason, answer, penalty: v === "wrong" || v === "skip" });
      s.log = [...s.log, { seat, image: c.image, verdict: v, heard, reason, answer }].slice(-400);
      if (dispatch(s, { t: "verdict", at: ctx.now, id: pending.id, v })) afterClock(s, ctx);
      return s;
    }
    return s;
  },

  tick(_room, s, ctx) {
    if (s.stage === "intro") {
      s.stage = "play";
      show(s, ctx);
      return s;
    }
    if (s.stage !== "play") {
      s.deadline = null;
      return s;
    }
    const c = s.clock;
    if (c.phase === "running") {
      if (s.speaking && s.speaking.until > ctx.now) {
        s.deadline = s.speaking.until;
        return s;
      }
      s.speaking = null;
      if (dispatch(s, { t: "tick", at: ctx.now })) afterClock(s, ctx);
      else schedule(s, ctx.now);
      return s;
    }
    if (c.phase === "transition") {
      show(s, ctx);
      return s;
    }
    if (c.phase === "checking" && c.pending) {
      // the recogniser never answered: a technical pause, nobody loses time
      feedback(s, { kind: "error", seat: c.active, heard: "", reason: "stt_error", penalty: false });
      dispatch(s, { t: "verdict", at: ctx.now, id: c.pending.id, v: "error" });
      s.deadline = null;
      return s;
    }
    s.deadline = null;
    return s;
  },

  onPause(_room, s, ctx) {
    if (s.stage === "play") {
      dispatch(s, { t: "pause", at: ctx.now, kind: "user" });
      s.speaking = null;
      s.deadline = null;
    }
    return s;
  },

  onResume(_room, s, pausedMs, ctx) {
    if (s.stage === "play") {
      if (s.clock.phase === "paused" && dispatch(s, { t: "resume", at: ctx.now })) afterResume(s, ctx);
    } else if (typeof s.deadline === "number") {
      s.deadline += pausedMs;
      if (s.introAt !== null) s.introAt += pausedMs;
    }
    return s;
  },

  onLeave(_room, s, playerId, ctx) {
    const seat = seatOf(s, playerId);
    if (seat < 0 || s.stage === "over" || s.clock.players[seat]?.out) return s;
    if (s.stage !== "play") {
      // before the first picture: with fewer than two players left the match is called off
      const left = s.seats.filter((_, i) => i !== seat && !s.clock.players[i].out).length;
      if (left < 2) {
        dispatch(s, { t: "abort", at: ctx.now });
        finish(s, ctx);
        return s;
      }
      dispatch(s, { t: "forfeit", at: ctx.now, player: seat });
      delete s.checked[playerId];
      if (s.stage === "check" && allChecked(s)) startIntro(s, ctx.now);
      return s;
    }
    if (dispatch(s, { t: "forfeit", at: ctx.now, player: seat })) {
      if (s.speaking?.seat === seat) s.speaking = null;
      if (s.clock.phase === "over") finish(s, ctx);
      else if (s.clock.phase === "transition") s.deadline = ctx.now + 700;
      else schedule(s, ctx.now);
    }
    return s;
  },

  view(room, s, viewerId, now): GuessView {
    const { handled: _handled, ...clock } = s.clock;
    const c = s.clock;
    // paused: the picture is hidden, so nobody gets extra time to study it
    const onScreen = s.stage === "play" && (c.phase === "running" || c.phase === "checking");
    const img = Math.min(c.image, Math.max(0, s.pictures.length - 1));
    return {
      stage: s.stage,
      category: s.category,
      seats: s.seats.map((p) => ({
        id: p.id,
        name: p.name,
        avatar: p.avatar,
        checked: !!s.checked[p.id],
        lang: s.checked[p.id] ?? null,
        present: room.players.some((x) => x.id === p.id),
      })),
      mySeat: viewerId ? seatOf(s, viewerId) : -1,
      clock,
      timeMs: GUESS_TIME_MS,
      penaltyMs: GUESS_PENALTY_MS,
      // during play: the pictures around the current one; afterwards: the ones that were answered
      window: s.stage === "over" ? [...new Set(s.log.map((e) => e.image))].map((i) => s.pictures[i]).filter(Boolean) : s.pictures.slice(Math.max(0, img - 1), img + 5),
      current: onScreen ? img : null,
      speaking: s.speaking && s.speaking.until > now ? s.speaking.seat : null,
      feedback: s.feedback,
      result: s.result,
      verified: s.verified,
      introAt: s.introAt,
      introMs: GUESS_INTRO_MS,
      log: s.stage === "over" ? s.log : [],
    };
  },

  isOver: (s) => s.stage === "over",

  summary(room, s): GameResultSummary | null {
    if (!s.result) return null; // called off: nothing to record
    const winner = s.result.winner;
    return {
      game: "guess",
      lang: room.lang,
      players: s.seats.map((p, i) => ({ id: p.id, name: p.name, score: s.clock.players[i]?.correct ?? 0, userId: p.userId, isBot: false })),
      winnerIds: winner !== null && winner >= 0 && s.seats[winner] ? [s.seats[winner].id] : [],
    };
  },
};
